import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useReadOnly, useSlateStatic } from 'slate-react';

import HelptapEditor from '../../helpers';
import { findPreviewBreakpoint } from '../../node-menu/layout-settings/preview-breakpoint';

import { beginGapDrag, endGapDrag, moveGapDrag } from './gap-drag';
import { measureGaps } from './gap-measure';
import GapHandle from './gap-handle';

function findDraggedGap(gaps, leftIndex) {

  for (let index = 0; index < gaps.length; index++) {
    if (gaps[index].leftIndex === leftIndex) {
      return gaps[index];
    }
  }

  return null;

}

// Spec 3.11: a handle in each gap of a row that doesn't wrap. The handles are measured from the
// cells, since gaps and widths differ by breakpoint.
function GapResizer(props) {

  const editor = useSlateStatic();
  const readOnly = useReadOnly();
  const rootReference = useRef(null);
  const sessionReference = useRef(null);
  const [gaps, setGaps] = useState([]);
  const [drag, setDrag] = useState(null);
  const measureReference = useRef(null);

  function measure() {

    const container = props.containerReference.current;
    let measured = [];

    if (readOnly === false && container !== null && HelptapEditor.layout.canResizeGaps(props.element, findPreviewBreakpoint(container)) === true) {
      measured = measureGaps(rootReference.current.parentNode, container);
    }

    if (JSON.stringify(measured) !== JSON.stringify(gaps)) {
      setGaps(measured);
    }

  }

  measureReference.current = measure;

  useLayoutEffect(measure);

  // Widths can change without the value changing, as when an image loads or the window resizes.
  useEffect(function onLoad() {

    const editorElement = rootReference.current.closest('.editor-element');

    function handlePointerEnter() {
      measureReference.current();
    }

    editorElement.addEventListener('pointerenter', handlePointerEnter);

    return function onUnload() {
      editorElement.removeEventListener('pointerenter', handlePointerEnter);
    };

  }, []);

  function handleStart(gap) {
    sessionReference.current = beginGapDrag(editor, props.element, props.containerReference.current, gap);
    setDrag({ leftIndex: gap.leftIndex, label: null });
  }

  function handleMove(delta) {

    if (sessionReference.current === null) {
      return;
    }

    const label = moveGapDrag(sessionReference.current, delta);
    const moved = measureGaps(rootReference.current.parentNode, props.containerReference.current);

    setGaps(moved);
    setDrag({ leftIndex: sessionReference.current.indexes.left, label });

  }

  function handleEnd() {

    const session = sessionReference.current;

    sessionReference.current = null;
    setDrag(null);

    if (session !== null) {
      endGapDrag(session);
    }

  }

  function renderHandles() {

    const handles = [];

    for (let index = 0; index < gaps.length; index++) {
      const active = drag !== null && drag.leftIndex === gaps[index].leftIndex;
      handles.push(<GapHandle key={index} gap={gaps[index]} active={active} onStart={handleStart} onMove={handleMove} onEnd={handleEnd} />);
    }

    return handles;

  }

  function tryRenderTooltip() {

    if (drag === null || drag.label === null || sessionReference.current === null) {
      return null;
    }

    const gap = findDraggedGap(gaps, sessionReference.current.indexes.left);

    if (gap === null) {
      return null;
    }

    return <div className="layout-gap-tooltip" style={{ left: gap.x + 'px', top: gap.top + 'px' }}>{drag.label}</div>;

  }

  let className = 'layout-gap-handles';

  if (drag !== null) {
    className = className + ' resizing';
  }

  return <div className={className} contentEditable={false} ref={rootReference}>
    {renderHandles()}
    {tryRenderTooltip()}
  </div>;

}

export default GapResizer;
